/** Public auth API paths (exact match after normalization). */
import axios from "axios";

export const PUBLIC_AUTH_API_PATHS = new Set([
  "/auth/login",
  "/auth/password-reset",
  "/auth/password-reset/confirm",
  "/auth/refresh",
]);

export type RequestUrlParts = {
  baseURL?: string;
  url?: string;
  /** Override for tests; defaults to `window.location.origin` in the browser. */
  documentOrigin?: string;
};

const ABSOLUTE_HTTP_URL_RE = /^https?:\/\//i;
const SCHEME_PREFIX_RE = /^[a-z][a-z\d+.-]*:/i;
const ABSOLUTE_HTTP_PATH_RE = /^https?:\/\/[^/\\?#]*([/\\][^?#]*)?/i;

function stripUrlControlWhitespace(value: string): string {
  return value.replace(/[\t\n\r]/g, "");
}

function isAbsoluteHttpUrl(value: string): boolean {
  return ABSOLUTE_HTTP_URL_RE.test(value);
}

function hasSchemeWithoutProperHttpUrl(value: string): boolean {
  if (!SCHEME_PREFIX_RE.test(value)) {
    return false;
  }
  return !isAbsoluteHttpUrl(value);
}

function normalizePathname(pathname: string): string {
  let path = pathname.trim();
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }
  path = path.replace(/\/{2,}/g, "/");
  if (path.length > 1 && path.endsWith("/")) {
    path = path.slice(0, -1);
  }
  return path;
}

function stripApiPrefix(pathname: string): string {
  for (const prefix of ["/api/v1", "/api"]) {
    if (pathname === prefix) {
      return "/";
    }
    if (pathname.startsWith(`${prefix}/`)) {
      return pathname.slice(prefix.length) || "/";
    }
  }
  return pathname;
}

function documentOrigin(config: RequestUrlParts): string {
  if (config.documentOrigin) {
    return config.documentOrigin;
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "http://localhost";
}

function pageHref(config: RequestUrlParts): string {
  const origin = documentOrigin(config);
  return origin.endsWith("/") ? origin : `${origin}/`;
}

function pathPortionForTraversalCheck(value: string, absolute: boolean): string {
  const sanitized = stripUrlControlWhitespace(value);
  if (absolute) {
    const withoutQuery = sanitized.split("?")[0].split("#")[0];
    const match = ABSOLUTE_HTTP_PATH_RE.exec(withoutQuery);
    if (!match) {
      return "/";
    }
    const pathPart = match[1] ?? "/";
    return pathPart.replace(/\\/g, "/");
  }
  return sanitized;
}

/** Detects `..` path segments after splitting on `/` or `\\` and percent-decoding. */
export function containsTraversalSegments(pathLike: string): boolean {
  const sanitized = stripUrlControlWhitespace(pathLike);
  for (const segment of sanitized.split(/[/\\]/)) {
    if (!segment) {
      continue;
    }
    try {
      if (decodeURIComponent(segment) === "..") {
        return true;
      }
    } catch {
      return true;
    }
  }
  return false;
}

function configHasTraversalSegments(config: RequestUrlParts): boolean {
  const rawUrl = (config.url ?? "").split("?")[0].split("#")[0];
  const baseURL = config.baseURL ?? "";

  if (rawUrl && hasSchemeWithoutProperHttpUrl(rawUrl)) {
    return false;
  }

  if (baseURL.length > 0) {
    const absoluteBase = isAbsoluteHttpUrl(stripUrlControlWhitespace(baseURL).trim());
    const basePath = pathPortionForTraversalCheck(baseURL, absoluteBase);
    if (containsTraversalSegments(basePath)) {
      return true;
    }
  }

  if (!rawUrl) {
    return false;
  }

  const absoluteUrl = isAbsoluteHttpUrl(stripUrlControlWhitespace(rawUrl).trim());
  const urlPath = pathPortionForTraversalCheck(rawUrl, absoluteUrl);
  return containsTraversalSegments(urlPath);
}

function resolveApiBaseUrl(config: RequestUrlParts): URL | null {
  try {
    const href = axios.getUri({
      baseURL: config.baseURL,
      url: "",
    });
    return new URL(href, pageHref(config));
  } catch {
    return null;
  }
}

/** Resolve the URL axios will request (same as `getUri` + page `location.href`). */
export function resolveRequestUrl(config: RequestUrlParts): URL | null {
  if (configHasTraversalSegments(config)) {
    return null;
  }

  const rawUrl = config.url ?? "";
  if (rawUrl && hasSchemeWithoutProperHttpUrl(rawUrl)) {
    return null;
  }

  try {
    const href = axios.getUri({
      baseURL: config.baseURL,
      url: config.url,
    });
    return new URL(href, pageHref(config));
  } catch {
    return null;
  }
}

/** Resolve request URL to a canonical pathname (no query/hash, no trailing slash). */
export function normalizeApiRequestPath(config: RequestUrlParts): string {
  const resolved = resolveRequestUrl(config);
  if (resolved) {
    return normalizePathname(resolved.pathname);
  }

  try {
    const href = axios.getUri({
      baseURL: config.baseURL,
      url: (config.url ?? "").split("?")[0].split("#")[0],
    });
    return normalizePathname(new URL(href, pageHref(config)).pathname);
  } catch {
    const rawUrl = (config.url ?? "").split("?")[0].split("#")[0];
    const baseURL = (config.baseURL ?? "").replace(/\/$/, "");
    const combined = rawUrl.startsWith("/")
      ? `${baseURL}${rawUrl}`
      : `${baseURL}/${rawUrl}`;
    return normalizePathname(combined);
  }
}

export function isPublicAuthApiPath(config: RequestUrlParts): boolean {
  const resolved = resolveRequestUrl(config);
  const apiBase = resolveApiBaseUrl(config);
  if (!resolved || !apiBase) {
    return false;
  }

  if (resolved.origin !== apiBase.origin) {
    return false;
  }

  const pathname = normalizePathname(resolved.pathname);
  const authRelative = stripApiPrefix(pathname);
  return PUBLIC_AUTH_API_PATHS.has(authRelative);
}

export function shouldSkipAuthRefreshOn401(config: RequestUrlParts): boolean {
  // Relative URLs with leading spaces stay protected, matching the path Axios actually requests.
  return isPublicAuthApiPath(config);
}
